
import { Question } from '@/types/assessment';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import SingleChoiceQuestion from './SingleChoiceQuestion';
import MultipleChoiceQuestion from './MultipleChoiceQuestion';
import SliderQuestion from './SliderQuestion';
import MaturityQuestion from './MaturityQuestion';
import { SimpleRichText } from '@/components/ui/simple-rich-text';
import { ChevronLeft, ChevronRight, BadgeDollarSign } from 'lucide-react';

interface QuestionRendererProps {
  question: Question;
  response?: {
    questionId: string;
    current: string[] | number;
    target: string[] | number;
  };
  onChange: (questionId: string, current: string[] | number, target: string[] | number) => void;
  onNext: () => void;
  onBack: () => void;
  canMoveForward: boolean;
  isLastQuestion: boolean;
}

const QuestionRenderer = ({ 
  question, 
  response, 
  onChange, 
  onNext, 
  onBack, 
  canMoveForward,
  isLastQuestion
}: QuestionRendererProps) => {
  const handleChange = (current: string[] | number, target: string[] | number) => {
    onChange(question.id, current, target);
  };

  const renderQuestionType = () => {
    const current = response?.current ?? (question.answerType === 'slider' ? 1 : []);
    const target = response?.target ?? (question.answerType === 'slider' ? 1 : []);

    switch (question.answerType) {
      case 'single':
        return (
          <SingleChoiceQuestion
            question={question}
            currentValue={current as string[]}
            targetValue={target as string[]}
            onChange={handleChange}
          />
        );
      case 'multiple':
        return (
          <MultipleChoiceQuestion
            question={question}
            currentValue={current as string[]}
            targetValue={target as string[]}
            onChange={handleChange}
          />
        );
      case 'slider':
        return (
          <SliderQuestion
            question={question}
            currentValue={current as number}
            targetValue={target as number}
            onChange={handleChange}
          />
        );
      case 'maturity':
        return (
          <MaturityQuestion
            question={question}
            currentValue={current as string[]}
            targetValue={target as string[]}
            onChange={handleChange}
          />
        );
      default:
        return <p>Unsupported question type</p>;
    }
  };

  return (
    <div className="relative min-h-[70vh] flex flex-col w-full">
      {/* Back button (left side) */}
      <Button
        variant="outline"
        onClick={onBack}
        size="icon"
        className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full h-12 w-12 z-10 flex items-center justify-center assessment-spring hover:scale-110 shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-elegant)] border-2"
      >
        <ChevronLeft className="h-5 w-5" />
      </Button>

      <div className="px-16 flex-1 flex flex-col"> {/* Add padding to make room for the side buttons */}
        {/* Question header - enhanced with brand styling */}
        <div className="assessment-question-card pb-4 pt-6 mb-6 animate-slide-in">
          <div className="mb-3 px-6 flex flex-wrap items-start gap-2">
            <h2 className="text-xl font-semibold text-foreground flex-1 min-w-[240px]">
              {question.text}
            </h2>
            {question.premium && (
              <Badge
                variant="secondary"
                className="shrink-0 gap-1 border border-primary/20 bg-primary/10 text-primary"
              >
                <BadgeDollarSign className="h-3 w-3" />
                Premium
              </Badge>
            )}
          </div>
          {question.explanation && (
            <div className="mb-4 border-l-4 border-primary/30 pl-4 py-2 bg-gradient-to-r from-primary/5 to-transparent rounded-r mx-6">
              <SimpleRichText 
                content={question.explanation}
                className="text-sm text-muted-foreground"
              />
            </div>
          )}
          {question.required && (
            <p className="text-sm text-muted-foreground px-6 flex items-center gap-1">
              <span className="w-1 h-1 bg-primary rounded-full"></span>
              Required question
            </p>
          )}
        </div>

        {/* Question content - using flex-1 to take remaining space */}
        <div className="flex-1 px-1">
          {renderQuestionType()}
        </div>
      </div>
      
      {/* Next button (right side) */}
      <Button
        onClick={onNext}
        size="icon"
        className="absolute right-0 top-1/2 -translate-y-1/2 rounded-full h-12 w-12 z-10 flex items-center justify-center assessment-spring hover:scale-110"
        style={{ 
          background: 'var(--gradient-primary)',
          boxShadow: 'var(--shadow-elegant)',
        }}
      >
        <ChevronRight className="h-5 w-5 text-white" />
      </Button>
    </div>
  );
};

export default QuestionRenderer;
